import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { STATUTS } from '../Takata';
import {
  ActionsContact, Bloc, CarteLigne, Fiche, Kv, LienOrdinateur, Liste, Pill, Plaque, Puces, Recherche,
  Segments, Statut, dateFr, depuis, messageErreur, numeroTel, useFicheOuverte, useToast,
} from './MobileUI';
import './TakataMobile.css';

// ═══════════════════════════════════════════════════════════════════════════
// Takata (rappel airbags) — version téléphone (2026-10-24), EN LECTURE.
//
// Le cas d'usage : un client appelle ou se présente, on retrouve son dossier
// au clavier (VIN, immat, nom, téléphone) et on le consulte. Aucune écriture
// depuis le téléphone : les seuls gestes sont Appeler (tel:) et copier le VIN.
// Tout le reste (journal, SMS, RDV, statut, Infos, pilotage) : version
// ordinateur.
//
// Routes dédiées, légères (le `GET /takata/dossiers` de l'ordinateur pèse
// ~17 Mo) :
//   - `GET /takata/mobile?q=&file=&concession_id=` : 50 cartes au plus,
//     recherche serveur, compteurs des files dans la même réponse ;
//   - `GET /takata/mobile/dossiers/:id` : dossier, propriétaire actuel, RDV,
//     10 dernières entrées du journal, dernier prêt de véhicule.
// Files (règles côté serveur, FILES_MOBILE_TAKATA) : Tout (défaut, dossiers
// actifs d'abord puis dernière activité), RDV à solder, RDV à venir (7 j),
// À rappeler, À relancer (15 j), Joignables.
// ═══════════════════════════════════════════════════════════════════════════

const FILES = [
  { cle: 'tout', libelle: 'Tout' },
  { cle: 'rdv_a_solder', libelle: 'RDV à solder', alerte: true },
  { cle: 'rdv_a_venir', libelle: 'RDV à venir' },
  { cle: 'a_rappeler', libelle: 'À rappeler', alerte: true },
  { cle: 'a_relancer', libelle: 'À relancer' },
  { cle: 'joignables', libelle: 'Joignables' },
];
const INTERTITRE = {
  rdv_a_solder: 'RDV passés, ni honorés ni no show',
  rdv_a_venir: 'Dans les 7 prochains jours',
  a_rappeler: 'À rappeler ou client qui relance',
  a_relancer: 'Sans action depuis plus de 15 jours',
  joignables: 'Téléphone connu, pas de RDV',
};
const FILE_RDV = new Set(['rdv_a_solder', 'rdv_a_venir']);

// Libellés repris de Takata.js (TYPES_CONTACT, RESULTATS_CONTACT, STATUTS_RDV).
const TYPES = {
  appel_entrant: 'Appel entrant', appel_sortant: 'Appel sortant', autres: 'Autres', courrier: 'Courrier',
  email: 'E-mail', sms: 'SMS', visite: 'Visite', no_show: 'No show',
};
const RESULTATS = {
  joint: 'Contacté', message_vocal_laisse: 'Message vocal laissé', pas_de_messagerie: 'Pas de messagerie',
  mauvais_numero: 'Mauvais numéro', changement_proprio: 'Chgt propriétaire', epave: 'Épave', detruit: 'Détruit',
  reforme: 'Réformé', vole: 'Volé', voyant_airbag: 'Voyant airbag', intervention_domicile: 'Intervention à domicile',
  remorquage: 'Remorquage', vh_pret: 'VH Prêt', client_relance: 'Le client relance', autres: 'Autres',
  messagerie: 'Messagerie', pas_de_reponse: 'Pas de réponse', accepte_rdv: 'Accepte RDV', refuse: 'Refus',
  courrier_envoye: 'Envoyé', distribue: 'Distribué', npai: 'NPAI', non_reclame: 'Non réclamé',
};
const RDV_STATUTS = {
  propose: { libelle: 'Proposé', ton: 'attente' },
  confirme: { libelle: 'Confirmé', ton: 'accent' },
  honore: { libelle: 'Honoré', ton: 'ok' },
  no_show: { libelle: 'No show', ton: 'ko' },
  reporte: { libelle: 'Reporté', ton: null },
  refuse: { libelle: 'Refusé', ton: 'ko' },
  annule: { libelle: 'Annulé', ton: null },
};
const CAMPAGNE = { termine: 'Campagne terminée', partiel: 'Campagne partielle', a_faire: 'Airbag à faire' };

const statutDe = (v) => STATUTS.find((s) => s.value === v) || { value: v, label: v || '—', color: 'var(--text-muted)' };
// Statut affiché tel quel : « Contacté » est un vrai statut depuis le 2026-10-26
// (trigger serveur), il remplace l'ancien relibellé « Leads ».
const libelleStatut = (v) => statutDe(v).label;
const vinCourt = (vin) => (vin ? `…${String(vin).slice(-6)}` : '');
const lieuRdv = (t) => (t === 'domicile' ? 'à domicile' : t === 'atelier' ? 'atelier' : '');

function jourHeure(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const j = d.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });
  return `${j} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
const jourCourt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Copie : l'API presse-papiers n'existe qu'en contexte sécurisé (https) ; repli
// sur execCommand pour l'accès de dev en http.
async function copier(texte) {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(texte); return true; }
  } catch (e) { /* repli ci-dessous */ }
  const zone = document.createElement('textarea');
  zone.value = texte;
  zone.setAttribute('readonly', '');
  zone.style.position = 'fixed';
  zone.style.opacity = '0';
  document.body.appendChild(zone);
  zone.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
  document.body.removeChild(zone);
  return ok;
}

function airbags(d) {
  const cote = (concerne, remplace, inspecte) => {
    if (!concerne) return 'non concerné';
    if (remplace) return 'remplacé';
    if (inspecte) return 'inspecté';
    return 'à faire';
  };
  return [
    ['Conducteur', cote(d.driv_airbag_concerned, d.driv_airbag_replaced, d.driv_airbag_inspected)],
    ['Passager', cote(d.pass_airbag_concerned, d.passenger_airbag_replaced, d.pass_airbag_inspected)],
  ];
}

export default function TakataMobile({ initialDossierId, onVersionOrdinateur }) {
  const [recherche, setRecherche] = useState('');
  const [file, setFile] = useState('tout');
  const [concession, setConcession] = useState('toutes');
  const [donnees, setDonnees] = useState({ dossiers: [], compteurs: {}, concessions: [] });
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [direct, setDirect] = useState(initialDossierId ? String(initialDossierId) : null);
  const [toast, afficher] = useToast();
  const appel = useRef(0);

  const charger = useCallback(async () => {
    const n = ++appel.current;
    setChargement(true);
    try {
      const params = { file, limit: 50 };
      if (recherche.trim()) params.q = recherche.trim();
      if (concession !== 'toutes') params.concession_id = concession;
      const r = await api.get('/takata/mobile', { params });
      if (n !== appel.current) return; // une réponse plus récente est attendue
      setDonnees(r.data);
      setErreur('');
    } catch (e) {
      if (n === appel.current) setErreur(messageErreur(e, 'Les dossiers n’ont pas pu être chargés.'));
    } finally {
      if (n === appel.current) setChargement(false);
    }
  }, [file, recherche, concession]);
  useEffect(() => { charger(); }, [charger]);

  const dossiers = donnees.dossiers || [];
  const cle = useCallback((d) => d.id, []);
  const fiche = useFicheOuverte(dossiers, cle);
  const compteurs = donnees.compteurs || {};
  const plusieurs = (donnees.concessions || []).length > 1;

  return (
    <div className="mb-page">
      <div className="mb-outils">
        <Recherche id="mb-tk-q" valeur={recherche} onChange={setRecherche} delai={300} placeholder="VIN, immat, nom, téléphone" />
        {plusieurs && (
          <Segments label="Concession" valeur={concession} onChange={setConcession}
            options={[{ cle: 'toutes', libelle: 'Toutes' }, ...donnees.concessions.map((c) => ({ cle: String(c.id), libelle: c.nom }))]} />
        )}
      </div>
      <Puces label="Files" valeur={file} onChange={setFile}
        options={FILES.map((f) => ({ ...f, compte: compteurs[f.cle] ?? null }))} />

      {donnees.hors_perimetre ? (
        <p className="mb-vide">Aucune concession Takata n’est configurée pour votre compte (onglet ⚙️ Configuration, sur ordinateur).</p>
      ) : (
        <Liste
          elements={dossiers}
          cle={cle}
          chargement={chargement}
          erreur={erreur}
          vide={recherche.trim() ? 'Aucun dossier ne correspond à cette recherche dans cette file.' : 'Aucun dossier dans cette file.'}
          groupe={(d) => (file === 'tout' ? (d.actif ? 'Dossiers en cours' : 'Terminés ou sortis') : INTERTITRE[file])}
          onOuvrir={fiche.ouvrir}
          carte={(d) => carteDossier(d, file)}
        />
      )}
      {!chargement && compteurs[file] > dossiers.length && (
        <p className="mb-aide mb-aide--centre">
          {dossiers.length} premiers sur {compteurs[file].toLocaleString('fr-FR')} : affinez la recherche.
        </p>
      )}
      <LienOrdinateur onClick={onVersionOrdinateur}>Saisie, RDV, SMS et tableaux de bord : version ordinateur</LienOrdinateur>

      {fiche.ouverte && (
        <FicheTakata
          key={fiche.ouverte.id}
          id={fiche.ouverte.id}
          ligne={fiche.ouverte}
          position={fiche.index}
          total={dossiers.length}
          onRetour={fiche.fermer}
          onPrecedent={fiche.precedent}
          onSuivant={fiche.suivant}
          afficher={afficher}
          onVersionOrdinateur={onVersionOrdinateur}
        />
      )}
      {!fiche.ouverte && direct && (
        <FicheTakata key={`direct-${direct}`} id={direct} onRetour={() => setDirect(null)}
          afficher={afficher} onVersionOrdinateur={onVersionOrdinateur} />
      )}
      {toast}
    </div>
  );
}

function carteDossier(d, file) {
  const s = statutDe(d.statut);
  const rdvTexte = d.rdv_date ? `RDV ${jourCourt(d.rdv_date)}${lieuRdv(d.rdv_type) ? ` · ${lieuRdv(d.rdv_type)}` : ''}` : '';
  let age;
  let retard = false;
  if (FILE_RDV.has(file) && d.rdv_date) {
    age = rdvTexte.replace(/^RDV /, '');
    retard = !!d.rdv_passe;
  } else if (d.der_action) {
    age = depuis(d.der_action);
    retard = file === 'a_relancer';
  } else {
    age = 'jamais contacté';
  }
  const dernier = d.der_contact_type
    ? [TYPES[d.der_contact_type] || d.der_contact_type, RESULTATS[d.der_contact_resultat]].filter(Boolean).join(' · ')
    : '';
  const rdv = d.rdv_statut ? RDV_STATUTS[d.rdv_statut] : null;
  return {
    couleur: s.color,
    contenu: (
      <>
        <CarteLigne
          avant={d.immat ? <Plaque immat={d.immat} /> : <span className="mb-tk-vin">{vinCourt(d.vin)}</span>}
          titre={d.proprietaire || 'Propriétaire inconnu'}
          age={age}
          retard={retard}
        />
        <span className="mb-carte-texte">
          {[[d.marque, d.modele].filter(Boolean).join(' '), rdvTexte || dernier].filter(Boolean).join(' · ')}
        </span>
        <span className="mb-carte-meta">
          <Statut libelle={`${d.statut === 'gele' ? '🔒 ' : ''}${s.label}`} couleur={s.color} />
          {rdv && <Pill ton={d.rdv_passe ? 'ko' : rdv.ton}>RDV {rdv.libelle.toLowerCase()}</Pill>}
          {d.stop_drive && d.actif && <Pill ton="attente">Stop drive</Pill>}
          {d.a_rappeler && <Pill ton="attente">À rappeler</Pill>}
          {d.campagne === 'partiel' && <Pill>Partiel</Pill>}
        </span>
      </>
    ),
  };
}

function FicheTakata({ id, ligne, position, total, onRetour, onPrecedent, onSuivant, afficher, onVersionOrdinateur }) {
  const [detail, setDetail] = useState(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let actif = true;
    api.get(`/takata/mobile/dossiers/${id}`)
      .then((r) => { if (actif) setDetail(r.data); })
      .catch((e) => { if (actif) setErreur(e?.response?.status === 404 ? 'Dossier introuvable ou hors de votre périmètre.' : messageErreur(e, 'Le dossier n’a pas pu être chargé.')); });
    return () => { actif = false; };
  }, [id]);

  const d = detail?.dossier;
  const titre = (d?.immat || ligne?.immat) || vinCourt(d?.vin || ligne?.vin) || `Dossier #${id}`;
  const proprio = detail?.proprietaire;
  const nomProprio = proprio ? [proprio.nom, proprio.prenom].filter(Boolean).join(' ') : '';
  const rdvs = detail?.rdv || [];
  const enCours = rdvs.filter((r) => r.statut === 'propose' || r.statut === 'confirme')
    .sort((a, b) => new Date(a.date_heure) - new Date(b.date_heure));
  const passes = rdvs.filter((r) => !enCours.includes(r));
  const maintenant = Date.now();

  const copierVin = async () => {
    afficher((await copier(d.vin)) ? 'VIN copié' : 'Copie impossible sur cet appareil');
  };

  return (
    <Fiche titre={titre} position={position} total={total} onRetour={onRetour}
      onPrecedent={onPrecedent} onSuivant={onSuivant} label={`Dossier Takata ${titre}`}>
      {erreur && <p className="mb-vide mb-vide--erreur">{erreur}</p>}
      {!erreur && !d && <p className="mb-vide">Chargement…</p>}
      {d && (
        <>
          <div className="mb-entete">
            <div className="mb-entete-haut">
              {d.immat && <Plaque immat={d.immat} />}
              {[[d.marque, d.modele].filter(Boolean).join(' '), vinCourt(d.vin)].filter(Boolean).join(' · ')}
            </div>
            <div>
              <div className="mb-entete-titre">{nomProprio || 'Propriétaire inconnu'}</div>
              <div className="mb-entete-sous">
                {proprio?.source === 'import' ? 'Coordonnées de l’import constructeur' : proprio ? 'Propriétaire actuel' : 'Aucune fiche propriétaire'}
                {proprio?.ville ? ` · ${proprio.ville}` : ''}
              </div>
            </div>
            <div className="mb-carte-meta">
              <Statut libelle={`${d.statut === 'gele' ? '🔒 ' : ''}${libelleStatut(d.statut)}`} couleur={statutDe(d.statut).color} />
              {d.stop_drive && <Pill ton="attente">Stop drive</Pill>}
              <Pill ton={d.campagne === 'termine' ? 'ok' : d.campagne === 'partiel' ? null : 'ko'}>{CAMPAGNE[d.campagne] || d.campagne}</Pill>
              {d.a_rappeler && <Pill ton="attente">À rappeler</Pill>}
              {d.accident_signale && <Pill ton="ko">Accident signalé</Pill>}
            </div>
            <div className="mb-tk-airbags">
              {airbags(d).map(([cote, etat]) => (
                <span key={cote} className={`mb-tk-airbag is-${etat === 'à faire' ? 'afaire' : etat === 'non concerné' ? 'nc' : 'fait'}`}>
                  {cote} : <b>{etat}</b>
                </span>
              ))}
            </div>
            <ActionsContact tel={proprio?.telephone} email={proprio?.email || undefined} />
            {(detail.autres_telephones || []).length > 0 && (
              <div className="mb-tk-autres">
                <span className="mb-etiquette">Autres numéros du dossier</span>
                {detail.autres_telephones.map((t) => (
                  <a key={t.telephone} className="mb-mini" href={`tel:${numeroTel(t.telephone)}`}>
                    📞 {t.telephone}{t.nom ? ` · ${t.nom}` : ''}
                  </a>
                ))}
              </div>
            )}
          </div>

          {enCours.length ? enCours.map((r) => {
            const st = RDV_STATUTS[r.statut] || { libelle: r.statut };
            const passe = new Date(r.date_heure).getTime() < maintenant;
            return (
              <div key={r.id} className={`mb-carton mb-tk-rdv${passe ? ' is-passe' : ''}`}>
                <div className="mb-tk-rdv-haut">
                  <b>RDV {jourHeure(r.date_heure)}{lieuRdv(r.type) ? ` · ${lieuRdv(r.type)}` : ''}</b>
                  <Pill ton={passe ? 'ko' : st.ton}>{st.libelle}</Pill>
                </div>
                {passe && <p className="mb-aide mb-tk-alerte">Date passée, RDV jamais soldé (honoré, no show…) : à faire sur ordinateur.</p>}
                {(r.secteur_geo || r.notes) && <p className="mb-aide">{[r.secteur_geo, r.notes].filter(Boolean).join(' · ')}</p>}
              </div>
            );
          }) : (
            <div className="mb-carton"><b>Aucun RDV en cours</b></div>
          )}

          {passes.length > 0 && (
            <Bloc titre="RDV passés" compte={passes.length}>
              <ul className="mb-tk-liste">
                {passes.map((r) => {
                  const st = RDV_STATUTS[r.statut] || { libelle: r.statut };
                  return (
                    <li key={r.id}>
                      <span>{jourHeure(r.date_heure)}{lieuRdv(r.type) ? ` · ${lieuRdv(r.type)}` : ''}</span>
                      <Pill ton={st.ton}>{st.libelle}</Pill>
                    </li>
                  );
                })}
              </ul>
            </Bloc>
          )}

          <Bloc titre="Journal" compte={detail.compteurs?.journal ?? detail.journal.length} ouvert
            resume={detail.journal[0] ? dateFr(detail.journal[0].date_heure) : 'vide'}>
            {detail.journal.length === 0 && <p className="mb-aide">Aucune entrée au journal.</p>}
            <ol className="mb-tk-chrono">
              {detail.journal.map((j) => (
                <li key={j.id}>
                  <b>{[TYPES[j.type] || j.type, RESULTATS[j.resultat]].filter(Boolean).join(' · ')}</b>
                  {j.est_premier_contact && <Pill ton="ok">1er contact</Pill>}
                  {j.notes && <p>{j.notes}</p>}
                  <small>{[j.auteur || (String(j.notes || '').startsWith('[Webapp]') ? 'Webapp terrain' : null), jourHeure(j.date_heure)].filter(Boolean).join(' · ')}</small>
                </li>
              ))}
            </ol>
            {(detail.compteurs?.journal || 0) > detail.journal.length && (
              <p className="mb-aide">Les {detail.journal.length} dernières entrées. Le journal complet : version ordinateur.</p>
            )}
          </Bloc>

          <Bloc titre="Prêt de véhicule"
            resume={!detail.pret ? '—' : detail.pret.propose
              ? `Accepté${detail.pret.date_debut ? ` du ${dateFr(String(detail.pret.date_debut).slice(0, 10))}` : ''}${detail.pret.date_fin ? ` au ${dateFr(String(detail.pret.date_fin).slice(0, 10))}` : ''}`
              : 'Refusé'}>
            {detail.pret ? (
              <Kv lignes={[
                ['Décision', detail.pret.propose ? 'Accepté' : 'Refusé'],
                detail.pret.propose && ['Véhicule prêté', detail.pret.immat_vh_pret],
                detail.pret.propose && ['Du', dateFr(String(detail.pret.date_debut || '').slice(0, 10))],
                detail.pret.propose && ['Au', dateFr(String(detail.pret.date_fin || '').slice(0, 10))],
                !detail.pret.propose && ['Motif', detail.pret.justification_si_non],
              ]} />
            ) : <p className="mb-aide">Aucune décision de prêt.</p>}
          </Bloc>

          <Bloc titre="Véhicule" resume={vinCourt(d.vin)}>
            <div className="mb-tk-vinligne">
              <code>{d.vin}</code>
              <button type="button" className="mb-mini" onClick={copierVin}>Copier</button>
            </div>
            <Kv lignes={[
              ['Immatriculation', d.immat],
              ['Concession', [d.concession_nom, d.location].filter(Boolean).join(' · ')],
              ['Production', dateFr(d.date_production)],
              ['1er contact', d.date_premier_contact ? dateFr(String(d.date_premier_contact).slice(0, 10)) : null],
              ['Créé le', dateFr(String(d.created_at || '').slice(0, 10))],
              ['Documents', String(detail.compteurs?.documents ?? 0)],
              ['Courriers', String(detail.compteurs?.courriers ?? 0)],
            ]} />
          </Bloc>

          <p className="mb-aide mb-aide--centre">Saisir une action, SMS, RDV, statut, documents et courriers : version ordinateur.</p>
          <LienOrdinateur onClick={onVersionOrdinateur} />
        </>
      )}
    </Fiche>
  );
}
